import { Fragment, useEffect, useState } from 'react'
import { useTranslation } from 'next-i18next'

import { InputDashboard } from '~/components/InputDashboard'
import useCurrentUser from '~/hooks/useCurrentUser'
import { useAppDispatch, useAppSelector } from '~/redux/hooks'
import { UserField } from '~/types/user'
import { countryData } from '~/shared/constants/locale.const'
import { EmailStatus } from '~/types/user-info-types'
import EmailVerification from '~/shared/components/EmailVerification/EmailVerification'
import {
  getCountryCallingCodeFromAlpha2Code,
  getCountryCodesFromPhoneNumber,
  isValidPhoneNumber,
  isValidPhoneNumberInput,
} from '~/utils/helpers'
import { useGetUserLocationInfoFromIpQuery } from '~/redux/apis/geo-location-api'

import { selectErrors, selectFields, setErrorsActionCreator, setFieldValueActionCreator } from '../redux/settings.slice'
import { InputPhoneNumber } from '../components/InputPhoneNumber'

export function PersonalInfoTab(): React.ReactElement {
  const dispatch = useAppDispatch()
  const { t } = useTranslation(['fields', 'validation'])

  const user = useCurrentUser()

  const [showEmailVerifySection, setShowEmailVerifySection] = useState<boolean>(false)

  const userEmail = user?.facebookEmail || user?.email || user?.authEmail
  const parsedBirthYear = Number.parseInt(user?.birthYear || '')
  const userBirthYear = isNaN(parsedBirthYear) ? null : parsedBirthYear
  const userWantsNewsletter = user?.wantsNewsletter
  const userPhone = user?.phone

  const [countryCode, setCountryCode] = useState('')
  const [countryCallingCode, setCountryCallingCode] = useState('')
  const [phoneWithoutCallingCode, setPhoneWithoutCallingCode] = useState('')

  const fields = useAppSelector(selectFields)
  const errors = useAppSelector(selectErrors)

  const { data } = useGetUserLocationInfoFromIpQuery()

  useEffect(() => {
    if (!user) return

    setShowEmailVerifySection(user?.emailStatus === EmailStatus.AUTO_VERIFY_SUCCESS)
  }, [user])

  useEffect(() => {
    if (userEmail) {
      dispatch(setFieldValueActionCreator({ fieldName: UserField.EMAIL, value: userEmail }))
    }
    if (userPhone) {
      dispatch(setFieldValueActionCreator({ fieldName: UserField.PHONE, value: userPhone }))
    }
    if (userBirthYear) {
      dispatch(setFieldValueActionCreator({ fieldName: UserField.BIRTH_YEAR, value: userBirthYear }))
    }
    if (userWantsNewsletter) {
      dispatch(setFieldValueActionCreator({ fieldName: UserField.WANTS_NEWSLETTER, value: userWantsNewsletter }))
    }
  }, [dispatch, userEmail, userBirthYear, userWantsNewsletter, userPhone])

  useEffect(() => {
    if (userPhone && isValidPhoneNumber(userPhone)) {
      const [tempCountryCallingCode, tempCountryCode] = getCountryCodesFromPhoneNumber(userPhone)
      setCountryCallingCode(tempCountryCallingCode)
      setCountryCode(tempCountryCode)
      setPhoneWithoutCallingCode(userPhone.split(':')[1])
    } else if (data?.countryCode) {
      const tempCountryCallingCode = getCountryCallingCodeFromAlpha2Code(data?.countryCode)
      setCountryCallingCode(tempCountryCallingCode ?? '+47')
      setCountryCode(data?.countryCode ?? 'NO')
      setPhoneWithoutCallingCode('')
    } else {
      setCountryCallingCode('+47')
      setCountryCode('NO')
      setPhoneWithoutCallingCode('')
    }
  }, [data?.countryCode, userPhone])

  useEffect(() => {
    if (phoneWithoutCallingCode) {
      dispatch(
        setFieldValueActionCreator({
          fieldName: UserField.PHONE,
          value: `${countryCallingCode}:${phoneWithoutCallingCode}`,
        }),
      )
    }
  }, [countryCallingCode, data?.countryCode, dispatch, phoneWithoutCallingCode])

  const handleOnChangeTextFields = (name: string, value: string): void => {
    dispatch(setFieldValueActionCreator({ fieldName: name, value }))
  }

  const onChangeEmail = (email: string): void => {
    dispatch(setFieldValueActionCreator({ fieldName: UserField.EMAIL, value: email }))

    if (email.length > 0) {
      dispatch(setErrorsActionCreator({ email: undefined }))
    }

    if (email !== userEmail) {
      setShowEmailVerifySection(false)
    } else {
      setShowEmailVerifySection(user?.emailStatus === EmailStatus.AUTO_VERIFY_SUCCESS)
    }
  }

  useEffect(() => {
    if (user?.emailStatus === EmailStatus.EMAIL_CHANGE_REQUIRED && fields.email === userEmail) {
      dispatch(setErrorsActionCreator({ email: t('validation:validEmail') }))
    } else {
      dispatch(setErrorsActionCreator({ email: undefined }))
    }
  }, [user, userEmail, fields.email, dispatch, t])

  const handleOnCountryCodeChange = (code: string): void => {
    const selectedCountry = countryData.find((country) => {
      return country.code === code
    })

    if (selectedCountry) {
      setCountryCallingCode(selectedCountry.callingCode)
      setCountryCode(selectedCountry.code)
    }
  }

  const handleOnPhoneNumberChange = (phoneNumber: string): void => {
    const numberWithoutCallerCode = phoneNumber.slice(countryCallingCode.length + 1)

    if (!numberWithoutCallerCode && data?.countryCode) {
      const tempCountryCallingCode = getCountryCallingCodeFromAlpha2Code(data?.countryCode)
      setCountryCallingCode(tempCountryCallingCode ?? '+47')
      setCountryCode(data?.countryCode ?? 'NO')
    } else if (!numberWithoutCallerCode) {
      setCountryCallingCode('+47')
      setCountryCode('NO')
    }

    if (isValidPhoneNumberInput(numberWithoutCallerCode)) {
      setPhoneWithoutCallingCode(numberWithoutCallerCode)
    }
  }

  return (
    <Fragment>
      <div>
        <InputDashboard
          autoComplete="email"
          errorMessage={errors?.email}
          label={t('fields:email')}
          name="email"
          onChange={(email) => onChangeEmail(email)}
          type="text"
          value={fields.email}
        />
        {showEmailVerifySection && !errors.email && user?.emailStatus !== EmailStatus.EMAIL_CHANGE_REQUIRED && (
          <EmailVerification email={userEmail} />
        )}
      </div>

      <InputPhoneNumber
        autoComplete="off"
        countryCode={countryCode}
        fieldErrors={errors.phone}
        label={t('fields:mobileNumber')}
        name="phone"
        setCountryCode={(newCode) => handleOnCountryCodeChange(newCode)}
        setValue={(val) => handleOnPhoneNumberChange(val)}
        type="text"
        value={`${countryCallingCode} ${phoneWithoutCallingCode}`}
      />

      <InputDashboard
        autoComplete="off"
        errorMessage={errors?.birthYear}
        label={t('fields:birthYear')}
        name="birthYear"
        onChange={(val) => handleOnChangeTextFields('birthYear', val)}
        type="text"
        value={fields.birthYear}
      />
    </Fragment>
  )
}
